import { anchorPosition } from '@/domain/construction';
import { type Scene } from '@/engine';

import { dashedIfCovered } from '../painters/hiddenLines';
import { paintName, paintValue, type PaintContext } from '../painters/paintValue';

import { decodeForCanvas } from './decodeImages';
import {
  type ContentOptions,
  drawList,
  exportFrame,
  type FrameOptions,
  projectorFor,
} from './drawList';

/**
 * Raster export.
 *
 * The one exporter that needs a browser, because rasterising is what a canvas is for. It
 * paints through the **same painters as the screen** rather than reimplementing them, so a
 * PNG cannot drift from what the user was looking at — which is the failure every separate
 * "export renderer" eventually has.
 *
 * ## Why it lives here and not in `infrastructure`
 *
 * The first attempt put it there, reasoning that a canvas is a browser thing and browser
 * things are adapters. The boundary lint rejected it, and the rule was right: rasterising is
 * *rendering*, and this layer already creates canvases — `LayerStack` makes one per layer.
 * What belongs in infrastructure is the part that has nothing to do with drawing, which is
 * handing the finished bytes to the browser as a download.
 *
 * The split that survived: **`rendering` produces a `Blob`, `infrastructure` downloads it.**
 */

export interface RasterOptions extends FrameOptions, ContentOptions {
  /**
   * Device pixels per output unit. Two gives a retina-sharp image, which is what a
   * projector or a printed worksheet needs.
   */
  readonly pixelRatio?: number;
  /**
   * Page background. Defaults to white — a transparent PNG dropped into a document with a
   * dark theme shows black strokes on black, and the user rarely wanted that.
   */
  readonly background?: string | null;
  readonly type?: 'image/png' | 'image/jpeg';
  readonly quality?: number;
}

export const DEFAULT_PIXEL_RATIO = 2;

/**
 * Renders the scene to an image blob.
 *
 * Rejects rather than resolving to `null` when the canvas cannot produce one: a caller that
 * silently downloads nothing is worse than one that reports a failure.
 */
export async function renderToBlob(scene: Scene, options: RasterOptions = {}): Promise<Blob> {
  const drawables = drawList(scene, options.only);
  const frame = exportFrame(drawables, options);
  const ratio = options.pixelRatio ?? DEFAULT_PIXEL_RATIO;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(frame.width * ratio));
  canvas.height = Math.max(1, Math.round(frame.height * ratio));

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('EduSketch: this browser gave no 2D context to export with');

  ctx.scale(ratio, ratio);

  const background = options.background === undefined ? '#ffffff' : options.background;
  if (background !== null) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, frame.width, frame.height);
  }

  /*
   * Images are decoded *before* painting rather than during it.
   *
   * On screen the cache can return nothing and let the next frame draw the picture; an export
   * has no next frame. Waiting here is what stops a PNG of a worksheet from arriving with the
   * images missing — which would be a silent, intermittent failure depending on whether the
   * browser happened to have them warm.
   */
  const decoded = await decodeForCanvas(drawables.map((drawable) => drawable.value));

  const toOutput = projectorFor(frame);
  const paint: PaintContext = {
    ctx,
    toScreen: (world) => toOutput(world),
    worldBounds: frame.world,
    zoom: frame.scale,
    images: { get: (href) => decoded.get(href) ?? null },
  };

  /*
   * The same four things the screen and the two vector exporters take off each drawable.
   *
   * This loop used to pass the value and the style and nothing else, which quietly dropped all
   * of the rest: a line the solid covers came out solid instead of dashed, a polygon's hidden
   * sides came out solid, a side lifted into a segment of its own was drawn twice, and **every
   * name was missing** — so an exported picture had no A, B or C on it at all.
   *
   * That is the drift `ARCHITECTURE.md` warns about when it says export is three emitters over
   * one draw list. The reason it survived is that `coverage.test.ts` asks its questions of the
   * two *vector* exporters, so nothing ever read this loop against the list's full contract.
   */
  for (const drawable of drawables) {
    paintValue(
      paint,
      drawable.value,
      dashedIfCovered(drawable.style, drawable.covered),
      drawable.coveredEdges,
      drawable.liftedEdges
    );

    // After the object it names, so a thick stroke never covers it.
    if (drawable.label !== undefined) {
      const at = anchorPosition(drawable.value);
      if (at !== null) {
        paintName(paint, at, drawable.label, drawable.style, drawable.nameDirection);
      }
    }
  }

  const type = options.type ?? 'image/png';
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, options.quality);
  });

  if (blob === null) throw new Error(`EduSketch: the browser could not encode ${type}`);
  return blob;
}
